import React, { useEffect, useState } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Badge } from '../../components/ui/badge';
import { Users, FileText, Layers, Eye, TrendingUp, Clock } from 'lucide-react';
import { analyticsAPI } from '../../lib/api';

const COLORS = ['#5BCEFA', '#F5A9B8', '#2D3748', '#48BB78', '#ED8936', '#9F7AEA'];

const STATUS_COLORS = {
  'En Revision': '#ECC94B',
  'Revisiones Requeridas': '#ED8936',
  'Aceptado': '#48BB78',
  'Rechazado': '#FC8181',
  'Publicado': '#5BCEFA'
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.get()
      .then(r => { if (r.data?.totals) setData(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex-1 flex justify-center items-center"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;
  if (!data) return <div className="flex-1 p-4 sm:p-6 lg:p-8"><p className="text-gray-400 font-sans">Error cargando analiticas.</p></div>;

  const subData = Object.entries(data.subscriptions || {}).map(([k, v]) => ({
    name: k === 'trial' ? 'Prueba' : k === 'active' ? 'Activa' : k === 'expired' ? 'Expirada' : k,
    value: v
  }));

  const statusData = (data.manuscripts_by_status || []).map(s => ({
    ...s,
    fill: STATUS_COLORS[s.status] || '#A0AEC0'
  }));

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto" data-testid="analytics-page">
      <div className="max-w-6xl">
        <div className="mb-8">
          <h1 className="text-2xl font-serif font-bold text-slate-dark">Analiticas</h1>
          <p className="text-sm font-sans text-gray-400 mt-1">Metricas y estadisticas de Archivos Trans</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Usuaries', value: data.totals.users, icon: Users, color: 'bg-trans-blue/10 text-trans-blue' },
            { label: 'Manuscritos', value: data.totals.manuscripts, icon: FileText, color: 'bg-trans-pink/10 text-trans-pink' },
            { label: 'Publicados', value: data.totals.articles, icon: Layers, color: 'bg-green-50 text-green-600' },
            { label: 'En Revision', value: data.totals.pending_reviews, icon: Eye, color: 'bg-yellow-50 text-yellow-600' },
          ].map(m => (
            <div key={m.label} className="p-5 bg-white border border-gray-100 rounded-sm" data-testid={`analytics-card-${m.label.toLowerCase()}`}>
              <div className={`w-9 h-9 rounded-md ${m.color} flex items-center justify-center mb-3`}>
                <m.icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <p className="text-2xl font-sans font-bold text-slate-dark">{m.value || 0}</p>
              <p className="text-xs font-sans text-gray-400 mt-1">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Manuscripts by Status */}
          <div className="bg-white border border-gray-100 rounded-sm p-5" data-testid="chart-manuscripts-status">
            <h3 className="font-serif font-semibold text-slate-dark mb-4">Manuscritos por Estado</h3>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="status" tick={{ fontSize: 11, fontFamily: 'sans-serif' }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontFamily: 'sans-serif', fontSize: 13 }} />
                  <Bar dataKey="count" name="Cantidad" radius={[4, 4, 0, 0]}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 font-sans py-8 text-center">Sin datos disponibles</p>
            )}
          </div>

          {/* Subscriptions Breakdown */}
          <div className="bg-white border border-gray-100 rounded-sm p-5" data-testid="chart-subscriptions">
            <h3 className="font-serif font-semibold text-slate-dark mb-4">Suscripciones</h3>
            {subData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={subData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {subData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontFamily: 'sans-serif', fontSize: 13 }} />
                  <Legend wrapperStyle={{ fontFamily: 'sans-serif', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 font-sans py-8 text-center">Sin datos disponibles</p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Monthly Registrations */}
          <div className="bg-white border border-gray-100 rounded-sm p-5" data-testid="chart-registrations">
            <h3 className="font-serif font-semibold text-slate-dark mb-4">Registros Mensuales</h3>
            {(data.monthly_registrations || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.monthly_registrations}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: 'sans-serif' }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontFamily: 'sans-serif', fontSize: 13 }} />
                  <Line type="monotone" dataKey="count" name="Registros" stroke="#5BCEFA" strokeWidth={2} dot={{ fill: '#5BCEFA', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 font-sans py-8 text-center">Sin datos disponibles</p>
            )}
          </div>

          {/* Articles by Category */}
          <div className="bg-white border border-gray-100 rounded-sm p-5" data-testid="chart-categories">
            <h3 className="font-serif font-semibold text-slate-dark mb-4">Articulos por Categoria</h3>
            {(data.articles_by_category || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.articles_by_category} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="categoria" tick={{ fontSize: 11, fontFamily: 'sans-serif' }} width={140} />
                  <Tooltip contentStyle={{ fontFamily: 'sans-serif', fontSize: 13 }} />
                  <Bar dataKey="count" name="Articulos" fill="#F5A9B8" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-gray-400 font-sans py-8 text-center">Sin datos disponibles</p>
            )}
          </div>
        </div>

        {/* Top Reviewers */}
        {(data.top_reviewers || []).length > 0 && (
          <div className="bg-white border border-gray-100 rounded-sm p-5" data-testid="top-reviewers">
            <h3 className="font-serif font-semibold text-slate-dark mb-4">Revisores Mas Activos</h3>
            <div className="space-y-2">
              {data.top_reviewers.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-trans-blue/10 flex items-center justify-center text-xs font-sans font-bold text-trans-blue">{i + 1}</span>
                    <span className="text-sm font-sans text-slate-dark">{r.name}</span>
                  </div>
                  <Badge variant="outline" className="text-xs font-sans">{r.count} revision{r.count !== 1 ? 'es' : ''}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
